import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Box, Button, Container, Modal, Stack, TextField, Toolbar, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { REQUIRED_RED } from '../../../App';
import { MuiTextFieldWithFocus } from '../../../config/MaterialUIConstants';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 3,
    px: 3,
    pb: 3,
};

const BusinessInfoModal = ({ open, onClose, onSave, initialData, isValueEditable = true, title = 'Business Info' }) => {
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            label: '',
            value: '',
        },
    });

    useEffect(() => {
        if (open && initialData) {
            setValue('label', initialData.label || '');
            setValue('value', initialData.value || '');
        } else if (open) {
            reset({ label: '', value: '' });
        }
    }, [open, initialData, setValue, reset]);

    const handleFormSubmit = (data) => {
        onSave(data);
        handleClose();
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={(event, reason) => {
                if (reason && reason === 'backdropClick') {
                    return;
                }
                handleClose();
            }}
            aria-labelledby="business-info-modal-title"
        >
            <Box sx={{ ...style, width: '500px', height: 'fit-content', borderRadius: '8px', padding: 0 }}>
                {/* Header */}
                <AppBar
                    position="static"
                    elevation={0}
                    sx={{
                        height: '56px',
                        backgroundColor: 'white',
                        color: 'black',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        padding: '0 16px',
                    }}
                >
                    <Toolbar disableGutters sx={{ minHeight: '56px !important', justifyContent: 'space-between' }}>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 500, letterSpacing: '0.15px', lineHeight: '160%', fontSize: '20px', color: 'rgba(0, 0, 0, 0.87)' }}
                        >
                            {title}
                        </Typography>
                        <CloseIcon onClick={handleClose} sx={{ cursor: 'pointer', fontSize: '32px', color: 'rgba(0, 0, 0, 0.87)' }} />
                    </Toolbar>
                </AppBar>

                {/* Content */}
                <Container sx={{ padding: '0 !important' }}>
                    <form onSubmit={handleSubmit(handleFormSubmit)}>
                        <Stack spacing={3} sx={{ padding: '24px !important' }}>
                            {/* Label Name */}
                            <Stack spacing={1}>
                                <Typography variant="body3" style={{ fontWeight: 500 }}>
                                    Label Name
                                    <span style={{ color: REQUIRED_RED, marginLeft: '4px' }}>*</span>
                                </Typography>
                                <Controller
                                    name="label"
                                    control={control}
                                    rules={{ required: 'Label name is required' }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            error={!!errors.label}
                                            helperText={errors.label?.message}
                                            sx={MuiTextFieldWithFocus}
                                        />
                                    )}
                                />
                            </Stack>

                            {/* Number / Value */}
                            {isValueEditable && (
                                <Stack spacing={1}>
                                    <Typography variant="body3" style={{ fontWeight: 500 }}>
                                        Number
                                        <span style={{ color: REQUIRED_RED, marginLeft: '4px' }}>*</span>
                                    </Typography>
                                    <Controller
                                        name="value"
                                        control={control}
                                        rules={{ required: 'Number is required' }}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                size="small"
                                                error={!!errors.value}
                                                helperText={errors.value?.message}
                                                sx={MuiTextFieldWithFocus}
                                            />
                                        )}
                                    />
                                </Stack>
                            )}
                        </Stack>

                        {/* Buttons */}
                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                            <Button variant="outlined" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained">
                                Save
                            </Button>
                        </Stack>
                    </form>
                </Container>
            </Box>
        </Modal>
    );
};

export default BusinessInfoModal;
